/**
 * Widget Configurator
 * Dynamic form for editing widget config and filters based on widget registry
 */

import { useState } from 'react';
import { getWidgetConfig } from './widgetRegistry';
import type { DashboardWidget } from './types';

interface Props {
  widget: DashboardWidget;
  onClose: () => void;
  onSave: (updates: Partial<DashboardWidget>) => void;
  isPending: boolean;
}

export default function WidgetConfigurator({ widget, onClose, onSave, isPending }: Props) {
  const widgetDef = getWidgetConfig(widget.type);
  
  const [title, setTitle] = useState(widget.title);
  const [config, setConfig] = useState(widget.config || {});
  const [filters, setFilters] = useState(widget.filters || {});

  if (!widgetDef) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          <p className="text-red-700">Unknown widget type: {widget.type}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-200 rounded-md">
            Close
          </button>
        </div>
      </div>
    );
  }

  const handleSave = () => {
    onSave({ title, config, filters });
  };

  const updateConfig = (key: string, value: any) => {
    setConfig({ ...config, [key]: value });
  };

  const updateFilter = (key: string, value: any) => {
    setFilters({ ...filters, [key]: value });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-900">Configure Widget</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isPending}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Widget Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Widget Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              placeholder="Enter widget title"
            />
          </div>

          {/* Config Fields */}
          {widgetDef.configFields.length > 0 && (
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-3">Configuration</h3>
              <div className="space-y-3">
                {widgetDef.configFields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                      {field.required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {field.type === 'number' ? (
                      <input
                        type="number"
                        value={config[field.key] ?? widgetDef.defaults.config[field.key] ?? ''}
                        onChange={(e) => updateConfig(field.key, Number(e.target.value))}
                        min={field.min}
                        max={field.max}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    ) : field.type === 'select' && field.options ? (
                      <select
                        value={config[field.key] ?? widgetDef.defaults.config[field.key] ?? ''}
                        onChange={(e) => updateConfig(field.key, e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={config[field.key] ?? widgetDef.defaults.config[field.key] ?? ''}
                        onChange={(e) => updateConfig(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filter Fields */}
          {widgetDef.filterFields.length > 0 && (
            <div>
              <h3 className="text-md font-semibold text-gray-900 mb-3">Filters (optional)</h3>
              <div className="space-y-3">
                {widgetDef.filterFields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    {field.type === 'number' ? (
                      <input
                        type="number"
                        value={filters[field.key] ?? widgetDef.defaults.filters[field.key] ?? ''}
                        onChange={(e) => updateFilter(field.key, Number(e.target.value))}
                        min={field.min}
                        max={field.max}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    ) : field.type === 'select' && field.options ? (
                      <select
                        value={filters[field.key] ?? widgetDef.defaults.filters[field.key] ?? ''}
                        onChange={(e) => updateFilter(field.key, e.target.value)}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      >
                        {field.options.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={filters[field.key] ?? widgetDef.defaults.filters[field.key] ?? ''}
                        onChange={(e) => updateFilter(field.key, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
